import React from 'react';
import { 
  ShoppingBag, BookOpen, Mail, Calendar, 
  TrendingUp, Image, Settings, Search,
  Plus, FileText, Users, Gift
} from 'lucide-react';

const iconMap = {
  products: ShoppingBag,
  courses: BookOpen,
  emails: Mail,
  bookings: Calendar,
  orders: TrendingUp,
  storefront: Image,
  settings: Settings,
  search: Search,
  default: FileText
};

/**
 * Reusable empty state component
 */
export function EmptyState({
  icon = 'default',
  title,
  description,
  actionLabel,
  onAction,
  className = ''
}) {
  const IconComponent = iconMap[icon] || iconMap.default;

  return (
    <div className={`text-center py-12 px-6 ${className}`}>
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <IconComponent className="text-gray-400" size={32} />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
      {description && (
        <p className="text-gray-600 mb-6 max-w-md mx-auto">{description}</p>
      )}
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition-all hover:shadow-lg"
        >
          <Plus size={18} />
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Empty state for products
 */
export function EmptyProducts({ onCreate }) {
  return (
    <EmptyState
      icon="products"
      title="No products yet"
      description="Create your first product to start selling. You can add digital downloads, courses, memberships, and more."
      actionLabel="Create Product"
      onAction={onCreate}
    />
  );
}

/**
 * Empty state for courses
 */
export function EmptyCourses({ onCreate }) {
  return (
    <EmptyState
      icon="courses"
      title="No courses yet"
      description="Build your first course to share your knowledge. Add modules, lessons, and content to create an engaging learning experience."
      actionLabel="Create Course"
      onAction={onCreate}
    />
  );
}

/**
 * Empty state for orders
 */
export function EmptyOrders() {
  return (
    <EmptyState
      icon="orders"
      title="No orders yet"
      description="When customers purchase your products, orders will appear here."
    />
  );
}

/**
 * Empty state for bookings
 */
export function EmptyBookings() {
  return (
    <EmptyState
      icon="bookings"
      title="No bookings yet"
      description="When clients book appointments, they'll appear here."
    />
  );
}

/**
 * Empty state for email templates
 */
export function EmptyEmailTemplates({ onCreate }) {
  return (
    <EmptyState
      icon="emails"
      title="No email templates yet"
      description="Create email templates to automate your communication. Use variables like {{name}} and {{orderId}} for personalization."
      actionLabel="Create Template"
      onAction={onCreate}
    />
  );
}

/**
 * Empty state for search results
 */
export function EmptySearch({ query, onClear }) {
  return (
    <div className="text-center py-12 px-6">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Search className="text-gray-400" size={32} />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">
        No results found
      </h3>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        We couldn't find anything matching "{query}". Try adjusting your search terms.
      </p>
      {onClear && (
        <button
          onClick={onClear}
          className="text-[#c5a059] hover:text-[#b08e4d] transition"
        >
          Clear search
        </button>
      )}
    </div>
  );
}

/**
 * Empty state for storefront sections
 */
export function EmptyStorefrontSections({ onAdd }) {
  return (
    <EmptyState
      icon="storefront"
      title="No sections yet"
      description="Add sections to build your storefront. Organize products, courses, and content to create a beautiful link-in-bio experience."
      actionLabel="Add Section"
      onAction={onAdd}
    />
  );
}



